import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChevronLeft, BookOpen, Target, Heart, Sparkles, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useSubscription, FEATURES } from '../hooks/useSubscription';
import UpgradeModal from '../components/UpgradeModal';
import { chunkedUpload } from '../utils/chunkedUpload';

import { StoriesTab } from './spiritual/StoriesTab';
import { GratitudeTab } from './spiritual/GratitudeTab';
import { PrayersTab } from './spiritual/PrayersTab';
import { GoalsTab } from './spiritual/GoalsTab';

const API = '';

const SPIRITUAL_BANNER = 'https://static.prod-images.emergentagent.com/jobs/6e8c71c9-5deb-44de-959d-34c14877fd56/images/d8bfab9d46afce1ab204900e45b94304853e649802759fee48187e3d4e31f875.jpeg';

export default function FamilySpiritual() {
  const { familyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const token = localStorage.getItem('session_token');
  const { hasFeature } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'stories');
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);

  // Stories
  const [stories, setStories] = useState([]);
  const [showStoryForm, setShowStoryForm] = useState(false);
  const [storyForm, setStoryForm] = useState({
    title: '', content: '', category: 'other', featured_family_member: '', time_period: '', tags: ''
  });
  const [storyVideo, setStoryVideo] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [videoError, setVideoError] = useState(null);
  const [editingStory, setEditingStory] = useState(null);

  // Gratitude
  const [gratitudes, setGratitudes] = useState([]);
  const [gratitudeInput, setGratitudeInput] = useState('');
  const [editingGratitude, setEditingGratitude] = useState(null);

  // Prayers
  const [prayers, setPrayers] = useState([]);
  const [showPrayerForm, setShowPrayerForm] = useState(false);
  const [prayerForm, setPrayerForm] = useState({ title: '', description: '', category: 'other', is_urgent: false });
  const [editingPrayer, setEditingPrayer] = useState(null);

  // Goals
  const [careerGoals, setCareerGoals] = useState([]);
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({ title: '', description: '', target_date: '' });

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [familyId, token]);

  const fetchData = async () => {
    try {
      const [familyRes, storiesRes, gratitudesRes, prayersRes, goalsRes] = await Promise.all([
        axios.get(`${API}/api/families/${familyId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/families/${familyId}/stories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/families/${familyId}/gratitude`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/families/${familyId}/prayer-requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/families/${familyId}/career-goals`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setFamily(familyRes.data);
      setStories(storiesRes.data);
      setGratitudes(gratitudesRes.data);
      setPrayers(prayersRes.data);
      setCareerGoals(goalsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ===== Story handlers =====
  const handleCreateStory = async (e) => {
    e.preventDefault();
    try {
      const data = {
        family_id: familyId,
        ...storyForm,
        tags: storyForm.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      const res = await axios.post(`${API}/api/stories`, data, { headers: { Authorization: `Bearer ${token}` } });

      let storyWithVideo = res.data;
      if (storyVideo) {
        setUploadingVideo(true);
        setVideoUploadProgress(0);
        try {
          const isSmall = storyVideo.size < 5 * 1024 * 1024;
          if (isSmall) {
            const formData = new FormData();
            formData.append('file', storyVideo);
            const videoRes = await axios.post(`${API}/api/stories/${res.data.story_id}/video`, formData, {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
            storyWithVideo = { ...storyWithVideo, video_url: videoRes.data.video_url };
          } else {
            const result = await chunkedUpload(storyVideo, {
              metadata: { family_id: familyId, upload_type: 'story_video', story_id: res.data.story_id },
              onProgress: (pct) => setVideoUploadProgress(pct),
            });
            storyWithVideo = { ...storyWithVideo, video_url: result.video_url };
          }
          setVideoUploadProgress(100);
        } catch (videoErr) {
          toast.error('Story saved, but video upload failed: ' + (videoErr.message || 'Please try again later.'));
        } finally {
          setUploadingVideo(false);
          setVideoUploadProgress(0);
        }
      }

      setStories([storyWithVideo, ...stories]);
      setShowStoryForm(false);
      setStoryForm({ title: '', content: '', category: 'other', featured_family_member: '', time_period: '', tags: '' });
      setStoryVideo(null);
      setVideoError(null);
      toast.success('Story shared!');
    } catch {
      toast.error('Failed to create story');
    }
  };

  const handleLikeStory = async (storyId) => {
    try {
      await axios.post(`${API}/api/stories/${storyId}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch {
      toast.error('Failed to like story');
    }
  };

  const handleEditStory = (story) => {
    setEditingStory(story);
    setStoryForm({
      title: story.title,
      content: story.content,
      category: story.category || 'other',
      featured_family_member: story.featured_family_member || '',
      time_period: story.time_period || '',
      tags: (story.tags || []).join(', ')
    });
    setShowStoryForm(true);
  };

  const handleUpdateStory = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/api/stories/${editingStory.story_id}`, {
        title: storyForm.title,
        content: storyForm.content,
        category: storyForm.category,
        featured_family_member: storyForm.featured_family_member,
        time_period: storyForm.time_period,
        tags: storyForm.tags.split(',').map(t => t.trim()).filter(t => t)
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowStoryForm(false);
      setEditingStory(null);
      setStoryForm({ title: '', content: '', category: 'other', featured_family_member: '', time_period: '', tags: '' });
      toast.success('Story updated!');
      fetchData();
    } catch {
      toast.error('Failed to update story');
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (!window.confirm('Are you sure you want to delete this story? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/stories/${storyId}`, { headers: { Authorization: `Bearer ${token}` } });
      setStories(stories.filter(s => s.story_id !== storyId));
      toast.success('Story deleted');
    } catch {
      toast.error('Failed to delete story');
    }
  };

  // ===== Gratitude handlers =====
  const handleCreateGratitude = async () => {
    if (!gratitudeInput.trim()) return;
    try {
      if (editingGratitude) {
        await axios.patch(`${API}/api/gratitude/${editingGratitude.gratitude_id}`, null, {
          params: { content: gratitudeInput },
          headers: { Authorization: `Bearer ${token}` }
        });
        setEditingGratitude(null);
        toast.success('Gratitude updated!');
        fetchData();
      } else {
        const res = await axios.post(`${API}/api/gratitude`, {
          family_id: familyId,
          content: gratitudeInput
        }, { headers: { Authorization: `Bearer ${token}` } });
        setGratitudes([res.data, ...gratitudes]);
        toast.success('Gratitude shared!');
      }
      setGratitudeInput('');
    } catch {
      toast.error('Failed to share gratitude');
    }
  };

  const handleEditGratitude = (g) => {
    setEditingGratitude(g);
    setGratitudeInput(g.content);
  };

  const handleDeleteGratitude = async (gratitudeId) => {
    if (!window.confirm('Are you sure you want to delete this gratitude entry?')) return;
    try {
      await axios.delete(`${API}/api/gratitude/${gratitudeId}`, { headers: { Authorization: `Bearer ${token}` } });
      setGratitudes(gratitudes.filter(g => g.gratitude_id !== gratitudeId));
      toast.success('Gratitude deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  // ===== Prayer handlers =====
  const handleCreatePrayer = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/api/prayer-requests`, {
        family_id: familyId,
        ...prayerForm
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPrayers([res.data, ...prayers]);
      setShowPrayerForm(false);
      setPrayerForm({ title: '', description: '', category: 'other', is_urgent: false });
      toast.success('Prayer request shared');
    } catch {
      toast.error('Failed to create prayer request');
    }
  };

  const handlePray = async (prayerId) => {
    try {
      await axios.post(`${API}/api/prayer-requests/${prayerId}/pray`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
      toast.success('Thank you for praying');
    } catch {
      toast.error('Failed to record prayer');
    }
  };

  const handleMarkAnswered = async (prayerId) => {
    try {
      await axios.patch(`${API}/api/prayer-requests/${prayerId}`, {
        is_answered: true,
        answered_note: 'Answered'
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
      toast.success('Marked as answered - Praise!');
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleEditPrayer = (prayer) => {
    setEditingPrayer(prayer);
    setPrayerForm({
      title: prayer.title,
      description: prayer.description || '',
      category: prayer.category || 'other',
      is_urgent: prayer.is_urgent || false
    });
    setShowPrayerForm(true);
  };

  const handleUpdatePrayer = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/api/prayer-requests/${editingPrayer.prayer_id}`, {
        title: prayerForm.title,
        description: prayerForm.description,
        category: prayerForm.category,
        is_urgent: prayerForm.is_urgent
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowPrayerForm(false);
      setEditingPrayer(null);
      setPrayerForm({ title: '', description: '', category: 'other', is_urgent: false });
      toast.success('Prayer request updated!');
      fetchData();
    } catch {
      toast.error('Failed to update prayer request');
    }
  };

  const handleDeletePrayer = async (prayerId) => {
    if (!window.confirm('Are you sure you want to delete this prayer request?')) return;
    try {
      await axios.delete(`${API}/api/prayer-requests/${prayerId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPrayers(prayers.filter(p => p.prayer_id !== prayerId));
      toast.success('Prayer request deleted');
    } catch {
      toast.error('Failed to delete prayer request');
    }
  };

  // ===== Goal handlers =====
  const handleCreateGoal = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/api/career-goals`, {
        family_id: familyId,
        ...goalForm
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCareerGoals([res.data, ...careerGoals]);
      setShowGoalForm(false);
      setGoalForm({ title: '', description: '', target_date: '' });
      toast.success('Goal created!');
    } catch {
      toast.error('Failed to create goal');
    }
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setGoalForm({
      title: goal.title,
      description: goal.description || '',
      target_date: goal.target_date || ''
    });
    setShowGoalForm(true);
  };

  const handleUpdateGoal = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/api/career-goals/${editingGoal.goal_id}`, {
        title: goalForm.title,
        description: goalForm.description,
        target_date: goalForm.target_date
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowGoalForm(false);
      setEditingGoal(null);
      setGoalForm({ title: '', description: '', target_date: '' });
      toast.success('Goal updated!');
      fetchData();
    } catch {
      toast.error('Failed to update goal');
    }
  };

  const handleUpdateGoalStatus = async (goalId, newStatus) => {
    try {
      await axios.patch(`${API}/api/career-goals/${goalId}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setCareerGoals(careerGoals.map(g => g.goal_id === goalId ? { ...g, status: newStatus } : g));
      const statusLabels = {
        'not_started': 'Not Started',
        'in_progress': 'In Progress',
        'on_hold': 'On Hold',
        'completed': 'Completed'
      };
      toast.success(`Goal status updated to "${statusLabels[newStatus]}"`);
    } catch {
      toast.error('Failed to update goal status');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    try {
      await axios.delete(`${API}/api/career-goals/${goalId}`, { headers: { Authorization: `Bearer ${token}` } });
      setCareerGoals(careerGoals.filter(g => g.goal_id !== goalId));
      toast.success('Goal deleted');
    } catch {
      toast.error('Failed to delete goal');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/family/${familyId}`)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white hover:border-slate-500 active:bg-slate-600"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Family
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">{family?.name}</h1>
              <p className="text-sm text-slate-400">Legacy, Mentorship & Spiritual</p>
            </div>
          </div>
        </div>
      </div>

      {/* Banner */}
      <div className="max-w-5xl mx-auto px-4 pt-6" data-testid="spiritual-banner">
        <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-lg">
          <img
            src={SPIRITUAL_BANNER}
            alt="A culturally mixed couple sitting together in peaceful prayer and reflection in a warm, meditative modern space"
            className="w-full h-auto"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="spiritual_features"
          requiredTier="seedling"
        />

        <Tabs value={activeTab} onValueChange={(tab) => {
          // Gate non-stories tabs for free users
          if (tab !== 'stories' && !hasFeature(FEATURES.SPIRITUAL_FEATURES)) {
            setShowUpgradeModal(true);
            return;
          }
          setActiveTab(tab);
        }}>
          <TabsList className="bg-slate-800 border border-slate-700 mb-6 flex-wrap">
            <TabsTrigger value="stories" className="data-[state=active]:bg-amber-600" data-testid="tab-stories">
              <BookOpen className="h-4 w-4 mr-2" />
              Stories
            </TabsTrigger>
            <TabsTrigger value="gratitude" className="data-[state=active]:bg-amber-600 relative" data-testid="tab-gratitude">
              <Heart className="h-4 w-4 mr-2" />
              Gratitude
              {!hasFeature(FEATURES.SPIRITUAL_FEATURES) && <Lock className="h-3 w-3 ml-1 text-amber-400" />}
            </TabsTrigger>
            <TabsTrigger value="prayers" className="data-[state=active]:bg-amber-600 relative" data-testid="tab-prayers">
              <Sparkles className="h-4 w-4 mr-2" />
              Prayers
              {!hasFeature(FEATURES.SPIRITUAL_FEATURES) && <Lock className="h-3 w-3 ml-1 text-amber-400" />}
            </TabsTrigger>
            <TabsTrigger value="career" className="data-[state=active]:bg-amber-600 relative" data-testid="tab-career">
              <Target className="h-4 w-4 mr-2" />
              Goals
              {!hasFeature(FEATURES.SPIRITUAL_FEATURES) && <Lock className="h-3 w-3 ml-1 text-amber-400" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stories">
            <StoriesTab
              stories={stories}
              showStoryForm={showStoryForm} setShowStoryForm={setShowStoryForm}
              storyForm={storyForm} setStoryForm={setStoryForm}
              storyVideo={storyVideo} setStoryVideo={setStoryVideo}
              uploadingVideo={uploadingVideo} videoUploadProgress={videoUploadProgress}
              videoError={videoError} setVideoError={setVideoError}
              editingStory={editingStory} setEditingStory={setEditingStory}
              onCreateStory={handleCreateStory} onUpdateStory={handleUpdateStory}
              onLikeStory={handleLikeStory} onEditStory={handleEditStory}
              onDeleteStory={handleDeleteStory}
              token={token}
            />
          </TabsContent>

          <TabsContent value="gratitude">
            <GratitudeTab
              gratitudes={gratitudes}
              gratitudeInput={gratitudeInput} setGratitudeInput={setGratitudeInput}
              editingGratitude={editingGratitude} setEditingGratitude={setEditingGratitude}
              onCreate={handleCreateGratitude}
              onEdit={handleEditGratitude}
              onDelete={handleDeleteGratitude}
            />
          </TabsContent>

          <TabsContent value="prayers">
            <PrayersTab
              prayers={prayers}
              showPrayerForm={showPrayerForm} setShowPrayerForm={setShowPrayerForm}
              prayerForm={prayerForm} setPrayerForm={setPrayerForm}
              editingPrayer={editingPrayer} setEditingPrayer={setEditingPrayer}
              onCreatePrayer={handleCreatePrayer} onUpdatePrayer={handleUpdatePrayer}
              onPray={handlePray} onMarkAnswered={handleMarkAnswered}
              onEditPrayer={handleEditPrayer} onDeletePrayer={handleDeletePrayer}
              currentUserId={user?.user_id} token={token}
            />
          </TabsContent>

          <TabsContent value="career">
            <GoalsTab
              goals={careerGoals}
              showGoalForm={showGoalForm} setShowGoalForm={setShowGoalForm}
              goalForm={goalForm} setGoalForm={setGoalForm}
              editingGoal={editingGoal} setEditingGoal={setEditingGoal}
              onCreateGoal={handleCreateGoal} onUpdateGoal={handleUpdateGoal}
              onUpdateStatus={handleUpdateGoalStatus}
              onEditGoal={handleEditGoal} onDeleteGoal={handleDeleteGoal}
              currentUserId={user?.user_id} token={token}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
